/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { navigateAction } from 'fluxible-router';
import { connect, useDispatch } from 'react-redux';

import { VerificationInput } from '../../../common/inputs/customs/verification-input';
import { initialState, resendReducer } from '../actions/reducer';
import { handleConfirmationCode } from '../actions/confirmation-code';
import FluxibleContext from '../../../fluxible-context';
import verification from '../actions/verify-code';
import { showToast } from '../../../../../components/toast/toastSlice';
import { VerificationErrorCode } from '../../../../../auth/authTypes';

import { TwoFactorAuthenticationFeedback } from './TwoFactorAuthenticationFeedback';
import styles from './TwoFactorAuthentication.module.scss';

import {
  createTwoFactorAuthCodeVerificationStatusPayload,
  createTwoFactorAuthResendCodePayload
} from 'app/scripts/components/moderation/preferences/settings-send-event-utils';
import { useSendEvent } from 'app/scripts/tracking/useSendEvent';
import { State, useTypedSelector } from 'state';
import logoutAccount from 'app/scripts/actions/account/logout';
import Icon from 'utils/components/icon';

const LOADING_STATE_TIMEOUT = 2500;
const SUCCESS_STATE_TIMEOUT = 5000;

interface TwoFactorAuthenticationProps {
  spotId: string;
  userEmail: string;
  userId: string;
  status: string;
  verifyCodeError: VerificationErrorCode;
  isToManyResentCode: boolean;
}

const MIN_CODE_LENGTH = 5;

const TwoFactorAuthentication = ({
  spotId,
  status,
  verifyCodeError,
  isToManyResentCode
}: TwoFactorAuthenticationProps) => {
  const [code, setCode] = useState<string>();
  const [currentStatus, setCurrentStatus] = useState<string>();
  const [resendState, dispatch] = useReducer(resendReducer, initialState);

  const lastErrorIsNetworkError = useRef(false);
  const requestWasSent = useRef(false);

  const isLoading = status === 'loading';
  const isToManyVerifyAttempts = verifyCodeError === 'ToManyVerifyAttempts';
  const isNetworkError = verifyCodeError === 'NetworkError';
  const isExpired = verifyCodeError === 'Expired';
  const canBeContinued = currentStatus === 'valid';
  const renderVerificationInput = !isExpired && !isToManyResentCode && !isToManyVerifyAttempts;
  const { id: userId, email: userEmail } = useTypedSelector(state => state.currentUser);

  const verificationTitle = isToManyVerifyAttempts ? 'Login temporarily blocked' : 'Verification required';

  const globalDispatch = useDispatch();

  useEffect(() => {
    let successTimeout: ReturnType<typeof setTimeout>;
    let hideSuccessTimeout: ReturnType<typeof setTimeout>;

    if (resendState.loading) {
      successTimeout = setTimeout(() => {
        dispatch({ type: 'RESEND_SUCCESS' });
      }, LOADING_STATE_TIMEOUT);
    }

    if (resendState.success) {
      hideSuccessTimeout = setTimeout(() => {
        dispatch({ type: 'RESEND_RESET' });
      }, SUCCESS_STATE_TIMEOUT);
    }

    return () => {
      if (successTimeout) clearTimeout(successTimeout);
      if (hideSuccessTimeout) clearTimeout(hideSuccessTimeout);
    };
  }, [resendState.loading, resendState.success]);

  const { executeAction } = useContext(FluxibleContext);

  const { sendEvent } = useSendEvent();

  const sendCodeVerificationStatusEvent = (status: boolean) => {
    sendEvent(createTwoFactorAuthCodeVerificationStatusPayload(status));
  };

  const dispatchEvent = (status: boolean) => {
    sendEvent(createTwoFactorAuthResendCodePayload(status));
  };

  useEffect(() => {
    sendEvent({
      type: '2FA_prompt',
      element: {
        item_name: '2FA prompt'
      }
    });
  }, []);

  useEffect(() => {
    if (!requestWasSent.current && userEmail) {
      requestWasSent.current = true;

      executeAction(handleConfirmationCode, {
        spotId,
        userEmail,
        dispatchEvent
      });
    }
  }, [executeAction, spotId, userEmail]);

  useEffect(() => {
    if (status === 'success') {
      executeAction(navigateAction, {
        url: `/spot/${spotId}/moderation`
      });
    }
    setCurrentStatus(status);
  }, [executeAction, spotId, status]);

  const onChange = useCallback(
    (value: string) => {
      const valueLength = value.trim().length;
      if (valueLength) {
        const isValidCode = value.length > MIN_CODE_LENGTH;
        const newStatus = isValidCode ? 'valid' : 'invalid';
        setCurrentStatus(newStatus);
        setCode(value);
      }
    },
    [setCurrentStatus, setCode]
  );

  const onVerify = useCallback(() => {
    if (currentStatus !== 'invalid') {
      executeAction(verification, { spotId, code, sendCodeVerificationStatusEvent });
    }
  }, [code, currentStatus, executeAction, sendCodeVerificationStatusEvent, spotId]);

  useEffect(() => {
    if (isNetworkError && !lastErrorIsNetworkError.current) {
      globalDispatch(
        showToast({
          type: 'error',
          message: 'Something went wrong',
          actionLabel: 'Try again',
          onAction: onVerify
        })
      );
      lastErrorIsNetworkError.current = true;
    } else {
      lastErrorIsNetworkError.current = false;
    }
  }, [isNetworkError, globalDispatch, onVerify]);

  const onResend = useCallback(() => {
    dispatch({ type: 'RESEND_REQUEST' });
    executeAction(handleConfirmationCode, {
      spotId,
      userEmail,
      dispatchEvent,
      isResend: true
    });
  }, [spotId]);

  const onLogout = () => {
    executeAction(logoutAccount, { spotId });
  };

  return (
    <div className={styles.form}>
      <div className={styles.mainContent}>
        <span className={styles.verificationTitle}>{verificationTitle}</span>

        {renderVerificationInput && (
          <div className={styles.contentWrapper}>
            <div className={styles.verificationTextSection}>
              <p className={styles.verificationText}>
                We sent an email with a temporary login code. Please check your inbox
              </p>
            </div>

            <div className={styles.codeInfo}>Code is valid for 10 minutes</div>

            <VerificationInput
              onVerify={onVerify}
              onChange={onChange}
              isFormDisabled={isLoading}
              status={currentStatus}
              verifyCodeError={verifyCodeError}
            />

            <button className={styles.verifyButton} onClick={onVerify} disabled={!canBeContinued || isLoading}>
              {isLoading ? <span className="spinner" /> : 'Verify'}
            </button>
          </div>
        )}

        <div className={styles.footerContainer}>
          <TwoFactorAuthenticationFeedback
            isToManyResentCode={isToManyResentCode}
            verifyCodeError={verifyCodeError}
            currentStatus={currentStatus}
            onResend={onResend}
            resendState={resendState}
          />

          {!isToManyVerifyAttempts && (
            <div className={styles.backLoginWrapper}>
              <a className={styles.backToLogin} onClick={onLogout}>
                <Icon name="chevron-left" className={styles.chevronLeft} />
                Back to Login
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state: State) {
  return {
    status: state.auth.verification.status,
    verifyCodeError: state.auth.verification.errorCode,
    isToManyResentCode: state.auth.verification.toManyResends
  };
}

export default connect(mapStateToProps)(TwoFactorAuthentication);
